import * as zod from 'zod';

// eslint-disable-next-line no-restricted-imports
import { ModelType } from '@sb/robot';
import { six } from '@sb/utilities';

// A set of torques for each joint in newton-meters
export const ArmJointTorques = zod.tuple([
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
]);

export type ArmJointTorques = zod.infer<typeof ArmJointTorques>;

// Torque values can be null if we couldnt read the torque
// Eventually we should handle null for all toques and other joint info
export const ArmJointTorquesNullable = zod.tuple([
  zod.number().nullable(),
  zod.number().nullable(),
  zod.number().nullable(),
  zod.number().nullable(),
  zod.number().nullable(),
  zod.number().nullable(),
]);

export type ArmJointTorquesNullable = zod.infer<typeof ArmJointTorquesNullable>;

export const ABSOLUTE_MAX_JOINT_TORQUES: ArmJointTorques = [
  433, 433, 204, 70, 70, 70,
];

export const COLLABORATIVE_MAX_JOINT_TORQUES: ArmJointTorques = [
  433, 433, 204, 70, 70, 70,
];

export const THOR_ABSOLUTE_MAX_JOINT_TORQUES: ArmJointTorques = [
  2678, 2678, 1530, 401, 401, 401,
];

export const THOR_COLLABORATIVE_MAX_JOINT_TORQUES: ArmJointTorques = [
  2678, 2678, 1530, 401, 401, 401,
];

export const DEFAULT_MAX_JOINT_TORQUES = ABSOLUTE_MAX_JOINT_TORQUES;

export const ABSOLUTE_MAX_TORQUE_SHOCK_COLLISION_THRESHOLDS: ArmJointTorques =
  six(1_000);

export const THOR_ABSOLUTE_MAX_TORQUE_SHOCK_COLLISION_THRESHOLDS: ArmJointTorques =
  six(5_000);

/**
 * The default joint torque thresholds for collision detection.
 * Collision threshold is the torque at which the joint is considered to be
 * in a collision state.
 * The default values are based on iterative testing on physical arm.
 */
export const DEFAULT_TORQUE_SHOCK_COLLISION_THRESHOLDS: ArmJointTorques = [
  120, 120, 100, 45, 45, 18,
];

export const THOR_DEFAULT_TORQUE_SHOCK_COLLISION_THRESHOLDS: ArmJointTorques = [
  400, 400, 300, 100, 100, 100,
];

export const SPARK_DEFAULT_TORQUE_SHOCK_COLLISION_THRESHOLDS: ArmJointTorques =
  [50, 100, 50, 30, 35, 15];

/**
 * The default torque-shock collision thresholds for a given robot model.
 * Spark and Thor carry model-specific tuning; every other model uses the
 * shared default.
 */
export function getDefaultTorqueShockCollisionThresholds(
  modelType: ModelType | undefined,
): ArmJointTorques {
  switch (modelType) {
    case ModelType.enum.Thor:
      return THOR_DEFAULT_TORQUE_SHOCK_COLLISION_THRESHOLDS;
    case ModelType.enum.Spark:
      return SPARK_DEFAULT_TORQUE_SHOCK_COLLISION_THRESHOLDS;
    default:
      return DEFAULT_TORQUE_SHOCK_COLLISION_THRESHOLDS;
  }
}
